import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import injectSheet from 'react-jss';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { formValueSelector, Field } from 'redux-form';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import IntegerInput from '../../../IntegerInput';
import Typography from '../../../Typography';
import FormattedCurrency from '../../../FormattedCurrency';
import DiscountCouponIcon from '../../../icons/DiscountCouponIcon';
import AccessCouponIcon from '../../../icons/AccessCoupon';

function PreOrderItem({ classes, ticketType, value, couponTicketsLeft }) {
  const {
    id,
    name,
    description,
    price,
    salePrice,
    limit,
    soldQuantity,
    dateRange: { start, end },
    groupLimit,
    active,
  } = ticketType;

  const now = moment();
  const unavailable =
    (start && now.isBefore(start, 'day')) ||
    (end && now.isAfter(end, 'day')) ||
    (limit && limit <= soldQuantity) ||
    (limit && groupLimit && groupLimit.min && groupLimit.min > limit - soldQuantity);
  const isOnSale = typeof salePrice === 'number' && !unavailable;
  const isFree = price === 0;

  let availability;
  if (unavailable) {
    let text;
    if (start && now.isBefore(start, 'day')) {
      text = <FormattedMessage id="soon" values={{ date: moment(start).format('L') }} />;
    } else if (end && now.isAfter(end, 'day')) {
      text = <FormattedMessage id="closed" />;
    } else {
      text = <FormattedMessage id="soldOut" />;
    }

    availability = (
      <Typography type="caption" className={classes.availability}>
        {text}
      </Typography>
    );
  } else {
    const maxes = [];

    if (limit) {
      maxes.push(limit - soldQuantity);
    }

    if (groupLimit && groupLimit.max) {
      maxes.push(groupLimit.max);
    }

    if (couponTicketsLeft !== null) {
      maxes.push(value + couponTicketsLeft);
    }

    const min = groupLimit && groupLimit.min;
    const max = maxes.length > 0 ? Math.min(...maxes) : null;

    availability = (
      <Field
        className={classes.availability}
        component={IntegerInput}
        name={`tickets.${id}`}
        min={min}
        max={max}
      />
    );
  }

  return (
    <div className={classNames(classes.root, unavailable && classes.unavailable)}>
      <div className={classes.ticketInfo}>
        <Typography type="body1" gutterBottom>
          <strong>{name}</strong>
          {isOnSale && active && <DiscountCouponIcon className={classes.icon} />}
          {!active && <AccessCouponIcon className={classes.icon} />}
        </Typography>
        {description && (
          <Typography type="caption" gutterBottom>
            {description}
          </Typography>
        )}
        <Typography className={classes.price} type="caption">
          <span
            className={classNames({
              [classes.oldPrice]: isOnSale && !isFree,
              [classes.free]: isFree,
            })}
          >
            {isFree ? <FormattedMessage id="freeTicket" /> : <FormattedCurrency value={price} />}
          </span>
          {isOnSale && !isFree && (
            <span className={classes.salePrice}>
              {salePrice ? (
                <FormattedCurrency value={salePrice} />
              ) : (
                <FormattedMessage id="freeTicket" />
              )}
            </span>
          )}
        </Typography>
      </div>
      {availability}
    </div>
  );
}

PreOrderItem.propTypes = {
  classes: PropTypes.object.isRequired,
  ticketType: PropTypes.object.isRequired,
  couponTicketsLeft: PropTypes.number,
  value: PropTypes.number,
};

PreOrderItem.defaultProps = {
  couponTicketsLeft: null,
  value: 0,
};

const stylesheet = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  icon: {
    marginLeft: theme.spacing.xsmall,
    color: theme.palette.success[500],
    verticalAlign: 'bottom',
  },
  unavailable: {
    '& p': {
      opacity: 0.6,
    },
  },
  ticketInfo: {
    padding: [[theme.spacing.medium, 0]],
  },
  price: {
    fontWeight: 'bold',
  },
  oldPrice: {
    color: theme.palette.common.minBlack,
    textDecoration: 'line-through',
  },
  free: {
    color: theme.palette.success[500],
  },
  salePrice: {
    marginLeft: theme.spacing.xsmall,
    color: theme.palette.success[500],
  },
  availability: {
    width: 130,
    minWidth: 120,
    textAlign: 'center',
  },
});

const orderFormSelector = formValueSelector('preOrder');
function mapStateToProps(state, ownProps) {
  return { value: orderFormSelector(state, `tickets.${ownProps.ticketType.id}`) };
}

export default compose(
  injectSheet(stylesheet),
  connect(mapStateToProps),
)(PreOrderItem);



// WEBPACK FOOTER //
// ./src/site-render/src/RegisterExtension/PreOrderForm/PreOrderItem/PreOrderItem.js